import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateTopicListInput,
  UpdateTopicListInput,
  TopicsQuery,
  TopicPlatform,
} from '../model/contracts'
import {
  fetchTopics,
  createTopicList,
  updateTopicList,
  deleteTopicList,
  addKeywordsToList,
} from '../api/topicsClient'

export function useTopicsController() {
  const queryClient = useQueryClient()

  const [query, setQuery] = useState<TopicsQuery>({
    page: 1,
    limit: 20,
    platform: 'all',
    search: '',
    sort: '-updatedAt',
  })

  const queryKey = ['topics', query]

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchTopics(query, signal),
  })

  const onSearchChange = useCallback((search: string) => {
    setQuery((prev) => ({ ...prev, search, page: 1 }))
  }, [])

  const onPlatformChange = useCallback((platform: string) => {
    setQuery((prev) => ({ ...prev, platform: platform as TopicPlatform, page: 1 }))
  }, [])

  const onSortChange = useCallback((field: string) => {
    setQuery((prev) => {
      const currentSort = prev.sort || ''
      const isDesc = currentSort.startsWith('-')
      const currentField = isDesc
        ? currentSort.slice(1)
        : currentSort.startsWith('+')
          ? currentSort.slice(1)
          : currentSort

      let nextSort = field
      if (currentField === field) {
        nextSort = isDesc ? `+${field}` : `-${field}`
      } else {
        nextSort = `+${field}`
      }
      return { ...prev, sort: nextSort, page: 1 }
    })
  }, [])

  const onPageChange = useCallback((page: number) => {
    setQuery((prev) => ({ ...prev, page }))
  }, [])

  const createMutation = useMutation({
    mutationFn: (input: CreateTopicListInput) => createTopicList(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (input: UpdateTopicListInput) => updateTopicList(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTopicList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
    },
  })

  const addKeywordsMutation = useMutation({
    mutationFn: ({ id, keywords }: { id: string; keywords: string[] }) =>
      addKeywordsToList(id, keywords),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
    },
  })

  return {
    topicLists: data?.data ?? [],
    total: data?.meta?.total ?? 0,
    page: data?.meta?.page ?? query.page,
    limit: data?.meta?.limit ?? query.limit,
    query,
    isLoading: isLoading || isPlaceholderData,
    error,
    onSearchChange,
    onPlatformChange,
    onSortChange,
    onPageChange,
    createTopicList: createMutation.mutate,
    updateTopicList: updateMutation.mutate,
    deleteTopicList: deleteMutation.mutate,
    addKeywords: addKeywordsMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isAddingKeywords: addKeywordsMutation.isPending,
  }
}
